import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Flask, Plus, ArrowRight, ArrowsClockwise } from "@phosphor-icons/react";
import NarrativeStrengthMeter from "../components/NarrativeStrengthMeter";

const STATUS_TONES = {
    draft:        { label: "Draft",        cls: "bg-muted text-muted-foreground" },
    validated:    { label: "Validated",    cls: "bg-foreground text-background" },
    challenged:   { label: "Challenged",   cls: "bg-accent/15 text-accent" },
    inconclusive: { label: "Inconclusive", cls: "bg-muted text-foreground/70" },
};

function StrengthMeter({ value }) {
    return <NarrativeStrengthMeter value={value} />;
}

export default function NarrativeLabs() {
    const [hypotheses, setHypotheses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [statement, setStatement] = useState("");
    const [notes, setNotes] = useState("");
    const navigate = useNavigate();

    const load = async () => {
        try {
            const r = await api.get("/labs/hypotheses");
            setHypotheses(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const submit = async () => {
        if (!statement.trim()) {
            toast.error("Hypothesis statement is required");
            return;
        }
        try {
            const r = await api.post("/labs/hypotheses", { statement: statement.trim(), notes: notes.trim() });
            toast.success("Hypothesis created");
            setOpen(false);
            setStatement(""); setNotes("");
            navigate(`/labs/${r.data.id}`);
        } catch {
            toast.error("Could not create hypothesis");
        }
    };

    return (
        <>
            <PageHeader
                overline="Strategic Intelligence"
                title="Narrative Labs"
                description="Pressure-test messaging ideas, emotional hypotheses, and positioning concepts against real conversations. The goal isn't confirmation — it's whether the idea is actually culturally true."
                testid="labs-header"
                actions={
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid="new-hypothesis-btn" size="sm" className="rounded-sm">
                                <Plus size={13} weight="bold" className="mr-1.5" /> New hypothesis
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="rounded-sm max-w-lg" data-testid="new-hypothesis-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display">New hypothesis</DialogTitle>
                                <p className="text-xs text-muted-foreground mt-1">
                                    A messaging idea, emotional claim, positioning concept, or cultural assumption to validate against real signals.
                                </p>
                            </DialogHeader>
                            <div className="space-y-3">
                                <textarea
                                    data-testid="hypothesis-statement"
                                    rows={3}
                                    placeholder='e.g. "Dating apps feel emotionally exhausting."'
                                    value={statement}
                                    onChange={(e) => setStatement(e.target.value)}
                                    className="w-full rounded-sm border border-input p-2 text-sm bg-background focus:outline-none focus:ring-1 focus:ring-primary font-display"
                                />
                                <Input
                                    data-testid="hypothesis-notes"
                                    placeholder="Optional context — what made you suspect this?"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    className="rounded-sm"
                                />
                            </div>
                            <DialogFooter>
                                <Button data-testid="confirm-new-hypothesis" onClick={submit} className="rounded-sm">
                                    Create
                                </Button>
                            </DialogFooter>
                        </DialogContent>
                    </Dialog>
                }
            />

            <PageBody>
                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : hypotheses.length === 0 ? (
                    <EmptyState
                        title="No hypotheses yet."
                        description="Start with a strategic claim — a positioning idea, founder narrative, ad hook, or cultural assumption — and the system will pressure-test it against the signals you've collected."
                        action={
                            <Button data-testid="empty-cta-new-hypothesis" onClick={() => setOpen(true)} className="rounded-sm">
                                <Flask size={14} weight="duotone" className="mr-1.5" /> Test your first hypothesis
                            </Button>
                        }
                        testid="labs-empty"
                    />
                ) : (
                    <div data-testid="hypotheses-grid" className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {hypotheses.map((h) => {
                            const tone = STATUS_TONES[h.status] || STATUS_TONES.draft;
                            const lr = h.last_run;
                            return (
                                <Link
                                    key={h.id}
                                    to={`/labs/${h.id}`}
                                    data-testid={`hypothesis-card-${h.id}`}
                                    className="block border border-border bg-card hover:border-foreground/30 transition-colors p-6 group"
                                >
                                    <div className="flex items-center justify-between mb-3">
                                        <span className={`overline text-[10px] tracking-[0.18em] px-2 py-0.5 rounded-sm ${tone.cls}`}>
                                            {tone.label}
                                        </span>
                                        <span className="font-mono text-[10px] text-muted-foreground tracking-wider">
                                            {h.runs_count > 0 ? `${h.runs_count} run${h.runs_count > 1 ? "s" : ""}` : "Untested"}
                                        </span>
                                    </div>
                                    <p className="font-display font-semibold text-[18px] leading-snug tracking-tight mb-4">
                                        "{h.statement}"
                                    </p>
                                    {lr ? (
                                        <>
                                            <div className="mb-3"><StrengthMeter value={lr.narrative_strength} /></div>
                                            <div className="flex flex-wrap gap-x-5 gap-y-1.5 text-[11px] font-mono text-muted-foreground">
                                                <span className="flex items-baseline gap-1.5">
                                                    <span className="overline text-[9px] tracking-[0.16em]">support</span>
                                                    <span className="text-foreground/85 tabular-nums">{lr.supporting_count}</span>
                                                </span>
                                                <span className="flex items-baseline gap-1.5">
                                                    <span className="overline text-[9px] tracking-[0.16em]">contra</span>
                                                    <span className="text-accent tabular-nums">{lr.contradictory_count}</span>
                                                </span>
                                                <span className="flex items-baseline gap-1.5">
                                                    <span className="overline text-[9px] tracking-[0.16em]">candidates</span>
                                                    <span className="text-foreground/85 tabular-nums">{lr.candidate_count}</span>
                                                </span>
                                                <span className="flex items-baseline gap-1.5">
                                                    <span className="overline text-[9px] tracking-[0.16em]">platforms</span>
                                                    <span className="text-foreground/85">{(lr.platforms || []).join(" · ") || "—"}</span>
                                                </span>
                                            </div>
                                        </>
                                    ) : (
                                        <p className="text-sm text-muted-foreground italic">
                                            Not yet pressure-tested. Open to validate.
                                        </p>
                                    )}
                                    <div className="flex items-center justify-end mt-4 pt-4 border-t border-border text-[11px] font-mono text-muted-foreground tracking-wider group-hover:text-foreground transition-colors">
                                        Open detail <ArrowRight size={12} className="ml-1" />
                                    </div>
                                </Link>
                            );
                        })}
                    </div>
                )}
            </PageBody>
        </>
    );
}
